import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Search, Calendar, Filter, ChevronRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const ColeccionLibros = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('todos');
  const [todosLosLibros, setTodosLosLibros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLibros = async () => {
      try {
  const response = await axios.get(`/data/libros.json?v=${Date.now()}`);
        setTodosLosLibros(response.data);
      } catch (error) {
        console.error('Error cargando libros:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLibros();
  }, []);

  const years = [...new Set(todosLosLibros.map(libro => libro.year))].sort((a, b) => b - a);

  const librosFiltrados = todosLosLibros.filter(libro => {
    const matchYear = selectedYear === 'todos' || libro.year === parseInt(selectedYear);
    const matchSearch = searchTerm === '' || 
      libro.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (libro.subtitulo && libro.subtitulo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (libro.autores && libro.autores.some(autor => autor.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (libro.coordinadores && libro.coordinadores.some(coord => coord.toLowerCase().includes(searchTerm.toLowerCase())));
    
    return matchYear && matchSearch;
  });

  const librosPorYear = librosFiltrados.reduce((acc, libro) => {
    if (!acc[libro.year]) {
      acc[libro.year] = [];
    }
    acc[libro.year].push(libro);
    return acc;
  }, {});

  const yearsConLibros = Object.keys(librosPorYear).sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-[#0b742f] mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600 text-lg">Cargando colección editorial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
              <BookOpen className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Colección Editorial CENID
              </h1>
              <p className="text-xl text-green-100">
                Catálogo completo de libros publicados
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por título, autor o tema..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none"
              />
            </div>
            <div className="md:w-64">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none appearance-none bg-white cursor-pointer"
                >
                  <option value="todos">Todos los años</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Mostrando <span className="font-semibold text-[#0b742f]">{librosFiltrados.length}</span> {librosFiltrados.length === 1 ? 'libro' : 'libros'}
            {selectedYear !== 'todos' && ` del año ${selectedYear}`}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Navegar por año</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {years.map(year => {
              const count = todosLosLibros.filter(l => l.year === year).length;
              return (
                <Link
                  key={year}
                  to={`/coleccion/${year}`}
                  className="bg-white hover:bg-[#0b742f] hover:text-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 group"
                >
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-[#0b742f] group-hover:text-white" />
                  <div className="text-2xl font-bold mb-1">{year}</div>
                  <div className="text-sm text-gray-600 group-hover:text-white/80">
                    {count} {count === 1 ? 'libro' : 'libros'}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="space-y-12">
          {yearsConLibros.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-md p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No se encontraron resultados
              </h3>
              <p className="text-gray-600">
                Intenta con otros términos de búsqueda o selecciona un año diferente.
              </p>
            </div>
          ) : (
            yearsConLibros.map(year => {
              const librosDelYear = librosPorYear[year];
              const totalLibrosYear = librosDelYear.length;
              const librosAMostrar = librosDelYear.slice(0, 3);
              const hayMas = totalLibrosYear > 3;

              return (
                <section key={year} className="space-y-6">
                  <div className="flex items-center justify-between border-b-2 border-[#0b742f] pb-3">
                    <h2 className="text-3xl font-bold text-gray-900">
                      Publicaciones {year}
                      <span className="ml-3 text-lg font-normal text-gray-600">
                        ({totalLibrosYear} {totalLibrosYear === 1 ? 'libro' : 'libros'})
                      </span>
                    </h2>
                    <Link
                      to={`/coleccion/${year}`}
                      className="text-[#0b742f] hover:text-[#085a24] font-semibold inline-flex items-center gap-2 transition-colors"
                    >
                      Ver todos de {year}
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  </div>

                  <div className="grid gap-6">
                    {librosAMostrar.map((libro) => (
                      <Link
                        key={libro.id}
                        to={`/coleccion/${year}`}
                        className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-[#0b742f] hover:scale-[1.02]"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            {libro.portada ? (
                              <img 
                                src={libro.portada} 
                                alt={`Portada de ${libro.titulo}`}
                                className="w-20 h-28 object-cover rounded shadow-md"
                              />
                            ) : (
                              <div className="w-20 h-28 bg-gradient-to-br from-[#0b742f] to-[#085a24] rounded flex items-center justify-center shadow-md">
                                <BookOpen className="w-10 h-10 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1 hover:text-[#0b742f]">
                              {libro.titulo}
                            </h3>
                            {libro.subtitulo && (
                              <p className="text-gray-600 italic mb-3">{libro.subtitulo}</p>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              {libro.coordinadores && libro.coordinadores.length > 0 && (
                                <span>
                                  <strong>Coordinadores:</strong> {libro.coordinadores.slice(0, 2).join(', ')}
                                  {libro.coordinadores.length > 2 && ` +${libro.coordinadores.length - 2} más`}
                                </span>
                              )}
                              {libro.autores && libro.autores.length > 0 && (
                                <span>
                                  <strong>Autores:</strong> {libro.autores.slice(0, 2).join(', ')}
                                  {libro.autores.length > 2 && ` +${libro.autores.length - 2} más`}
                                </span>
                              )}
                              {libro.isbn_cenid && (
                                <span><strong>ISBN:</strong> {libro.isbn_cenid}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {hayMas && (
                    <div className="text-center pt-4">
                      <Link
                        to={`/coleccion/${year}`}
                        className="inline-flex items-center gap-2 bg-[#0b742f] hover:bg-[#085a24] text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-md"
                      >
                        Ver todos los {totalLibrosYear} libros de {year}
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>

        <div className="mt-16 bg-gradient-to-r from-[#0b742f] to-[#085a24] rounded-2xl p-8 text-white">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">{todosLosLibros.length}</div>
              <div className="text-green-100">Libros publicados</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">{years.length}</div>
              <div className="text-green-100">Años de publicación</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">
                {new Set(todosLosLibros.flatMap(l => [...(l.autores || []), ...(l.coordinadores || [])])).size}+
              </div>
              <div className="text-green-100">Autores y coordinadores</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ColeccionLibros;
